import { useEffect, useState } from "react";
import { ApiError, createBillingCheckout, createBillingPortal, decideBillingSeatRequest, getBillingSummary, getCompanyTracking, type BillingSummary, type CheckoutSelection, type TrackingSummary, getMemberReferralProgram, type MemberReferralProgram } from "./api";
import { useAuth } from "./auth";

const current = (status: string) => ["active", "trialing", "past_due"].includes(status);

export function BillingPage() {
  const company = useAuth().session!.companies[0]!;
  const [billing, setBilling] = useState<BillingSummary>();
  const [trackingOptions, setTrackingOptions] = useState<TrackingSummary>();
  const [referrals,setReferrals]=useState<MemberReferralProgram>();
  const [copied,setCopied]=useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [voucherCode, setVoucherCode] = useState("");
  async function refresh() {
    const [billingSummary, trackingSummary,referralSummary] = await Promise.all([getBillingSummary(company.companyId), getCompanyTracking(company.companyId),getMemberReferralProgram(company.companyId)]);
    setBilling(billingSummary);
    setTrackingOptions(trackingSummary);
    setReferrals(referralSummary);
  }
  useEffect(() => {
    void refresh().catch((error) => setError(message(error)));
  }, [company.companyId]);
  async function checkout(selection: CheckoutSelection, key: string) {
    setBusy(key);
    setError("");
    try {
      window.location.assign(
        (
          await createBillingCheckout(company.companyId, {
            ...selection,
            ...(voucherCode.trim() ? { voucherCode: voucherCode.trim().toUpperCase() } : {}),
          })
        ).url,
      );
    } catch (error) {
      setError(message(error));
      setBusy("");
    }
  }
  async function portal() {
    setBusy("portal");
    try {
      window.location.assign((await createBillingPortal(company.companyId)).url);
    } catch (error) {
      setError(message(error));
      setBusy("");
    }
  }
  async function decide(id: string, approve: boolean) {
    setBusy(id);
    setError("");
    try {
      await decideBillingSeatRequest(company.companyId, id, approve);
      await refresh();
    } catch (error) {
      setError(message(error));
    } finally {
      setBusy("");
    }
  }
  const full = billing?.subscriptions.find((item) => item.kind === "full_account" && current(item.status));
  const tracking = billing?.subscriptions.find((item) => item.kind === "tracking_connect" && current(item.status));
  return (
    <>
      <header className="team-header">
        <div>
          <h1 className="h3 mb-1">Billing</h1>
          <p className="text-secondary mb-0">Owner-only subscriptions, payments, renewals and approvals.</p>
        </div>
        {billing && <span className="status-chip status-active">{billing.environment === "live" ? "Live billing" : "Sandbox billing"}</span>}
      </header>
      {error && <div className="alert alert-danger">{error}</div>}
      {billing?.accessStatus === "grace" && <div className="alert alert-warning">Payment needs attention. Existing work remains available during the {billing.graceDays}-day grace period.</div>}
      {billing?.accessStatus === "read_only" && <div className="alert alert-danger">This account is read-only. Existing accepted work, PODs and invoices remain accessible.</div>}
      {referrals&&<section className="team-panel mb-3" id="member-referrals">
        <div className="admin-section-heading"><div><h2 className="h5">Refer five paying companies. Get one month free.</h2><p className="text-secondary small mb-0">Share your personal referral link. When five separate companies join and pay for their own LoadSub memberships, your company receives one free membership month.</p></div></div>
        <p className="small mt-3 mb-0">A great way to grow your referrals is to create your own private group. Ditch the WhatsApp confusion and run your group’s loads through LoadSub—with one shared load board, clear assignments, organised messages and a proper record of every job.</p>
        <div className="progress mt-3" role="progressbar" aria-label="Progress to next free month" aria-valuemin={0} aria-valuemax={5} aria-valuenow={referrals.progress}><div className="progress-bar" style={{width:`${referrals.progress/5*100}%`}} /></div>
        <p className="small mt-2">{referrals.progress} of 5 paying companies towards the next free month · {referrals.trialling} currently trialling</p><p className="text-secondary small mt-1">Referred companies appear here when they register. They count toward your free month after their first successful membership payment. {referrals.rewards.filter(item=>item.status==="applied").length} free months applied · {referrals.rewards.filter(item=>item.status==="pending").length} pending.</p>
        <div className="input-group"><input className="form-control" readOnly value={referrals.link} aria-label="Your referral link"/><button className="btn btn-outline-light" type="button" onClick={()=>void navigator.clipboard.writeText(referrals.link).then(()=>{setCopied(true);window.setTimeout(()=>setCopied(false),1800);})}>{copied?"Copied":"Copy link"}</button></div>
        <p className="text-secondary small mt-2 mb-0">Managed or sponsored subcontractors only count if they purchase and pay for their own membership. Refunds do not affect an earned referral. Chargebacks reverse qualification.</p>
        {referrals.source.memberCommissionQualifiedAt
          ? <p className="small mt-3 mb-0"><strong>Commission programme active.</strong> Companies referred through this link after you qualified earn your company 10% of their membership fees during their first 12 paid months. £{(referrals.commission.commissionPence/100).toFixed(2)} commission recorded.</p>
          : <p className="small mt-3 mb-0"><strong>Keep going:</strong> earn 12 free membership months to unlock 10% commission on future referrals for their first 12 paid months. Only companies referred after you qualify earn commission.</p>}
        {referrals.referrals.length>0&&<details className="mt-3"><summary>Referral history</summary><div className="table-responsive mt-2"><table className="table table-dark"><thead><tr><th>Company</th><th>Status</th><th>Joined</th><th>Qualified</th></tr></thead><tbody>{referrals.referrals.map(item=><tr key={item.referred_company_id}><td>{item.trading_name}</td><td>{item.status==="not_converted"?"Trial ended — not subscribed":item.status==="qualified"?"Paying — qualified":item.status.charAt(0).toUpperCase()+item.status.slice(1)}</td><td>{new Date(item.joined_at).toLocaleDateString("en-GB")}</td><td>{item.qualified_at?new Date(item.qualified_at).toLocaleDateString("en-GB"):"—"}</td></tr>)}</tbody></table></div></details>}
      </section>}
      {!billing?.subscriptions.some((item) => current(item.status)) && (
        <div className="team-panel mb-3">
          <label className="form-label" htmlFor="billing-voucher">
            Voucher code <span className="text-secondary">· optional</span>
          </label>
          <input id="billing-voucher" className="form-control" value={voucherCode} maxLength={24} autoCapitalize="characters" onChange={(event) => setVoucherCode(event.target.value)} placeholder="Enter voucher before choosing a plan" />
        </div>
      )}
      {billing?.changeRequests.some((item) => item.status === "pending") && (
        <section className="team-panel mb-3">
          <h2 className="h5">Approval requests</h2>
          {billing.changeRequests
            .filter((item) => item.status === "pending")
            .map((item) => (
              <article className="invoice-charge" key={item.id}>
                <div>
                  <strong>10 more seats · {item.groupName}</strong>
                  <small>
                    Requested by {item.requestedByEmail} · {item.currentQuantity} → {item.requestedQuantity} seats
                  </small>
                </div>
                <b>+£{(item.monthlyDeltaPence / 100).toFixed(0)}/month + VAT</b>
                <span>
                  <button className="btn btn-sm btn-success" disabled={!!busy} onClick={() => void decide(item.id, true)}>
                    Approve
                  </button>
                  <button className="btn btn-sm btn-outline-danger" disabled={!!busy} onClick={() => void decide(item.id, false)}>
                    Reject
                  </button>
                </span>
              </article>
            ))}
        </section>
      )}
      <section className="team-panel mb-3">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5">Full company account</h2>
            <p className="text-secondary small mb-0">Introductory price £7.50/month for 6 months, then £15. Or £90 for the first year, renewing at £150. LoadSub never takes a cut of work.</p>
          </div>
          {full && <Status item={full} />}
        </div>
        {full ? (
          <button className="btn btn-outline-light mt-3" disabled={!!busy} onClick={() => void portal()}>
            Manage plan, payment and cancellation
          </button>
        ) : (
          <div className="d-flex gap-2 mt-3">
            <button className="btn btn-primary" disabled={!!busy} onClick={() => void checkout({ kind: "full_account", interval: "month" }, "month")}>
              £7.50 monthly · first 6 months
            </button>
            <button className="btn btn-outline-light" disabled={!!busy} onClick={() => void checkout({ kind: "full_account", interval: "year" }, "year")}>
              £90 · first year
            </button>
          </div>
        )}
      </section>
      <section className="team-panel mb-3" id="tracking-connect">
        <div className="admin-section-heading">
          <div>
            <h2 className="h5">Tracking Connect</h2>
            <p className="text-secondary small mb-0">One subscription for automatic load visibility and private nearby-load suggestions: connect supported telematics or use the driver's device GPS with no additional tracking system. £10/month for up to 30 tracked vehicles, £20 for 31–50, or £30 for 51+.</p>
          </div>
          {tracking && <Status item={tracking} />}
        </div>
        {trackingOptions && (
          <div className="tracking-billing-availability">
            <p><strong>LoadSub Device GPS:</strong> requires the LoadSub PWA and location permission. It shares only the GPS location of the device the driver is using during an active load.</p>
            <p><strong>Available now:</strong> {trackingOptions.providers.filter((provider) => provider.enabled).map((provider) => provider.displayName).join(", ") || "No providers yet"}.</p>
            <p><strong>Coming soon:</strong> {trackingOptions.providers.filter((provider) => !provider.enabled).map((provider) => provider.displayName).join(", ") || "None"}.</p>
          </div>
        )}
        {tracking ? (
          <button className="btn btn-outline-light mt-3" disabled={!!busy} onClick={() => void portal()}>
            Manage Tracking Connect
          </button>
        ) : (
          <button className="btn btn-primary mt-3" disabled={!!busy || trackingOptions?.providers.every((provider) => !provider.enabled)} onClick={() => void checkout({ kind: "tracking_connect" }, "tracking-connect")}>
            {busy === "tracking-connect" ? "Opening checkout…" : "Add Tracking Connect"}
          </button>
        )}
        <a className="btn btn-link mt-3" href="/company/settings#fleet-tracking">See provider status or register interest</a>
      </section>
      {billing?.billableGroups.map((group) => (
        <GroupBilling key={group.id} group={group} billing={billing} busy={busy} checkout={checkout} portal={portal} />
      ))}
      {billing?.subscriptions.length ? (
        <section className="team-panel mb-3">
          <div className="admin-section-heading">
            <h2 className="h5">Subscriptions</h2>
            <button className="btn btn-outline-light" onClick={() => void portal()}>
              Open Stripe billing portal
            </button>
          </div>
          <dl className="integration-details">
            {billing.subscriptions.map((item) => (
              <div key={item.id}>
                <dt>{item.groupName ?? "Full account"}</dt>
                <dd>
                  <Status item={item} />
                  {item.currentPeriodEnd ? ` · ${item.cancelAtPeriodEnd ? "ends" : "renews"} ${new Date(item.currentPeriodEnd).toLocaleDateString("en-GB")}` : ""}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      {billing?.invoices.length ? (
        <section className="team-panel">
          <h2 className="h5">Stripe invoices and receipts</h2>
          <div className="table-responsive">
            <table className="table table-dark">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>For</th>
                  <th>Status</th>
                  <th>Amount</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map((item) => (
                  <tr key={item.id}>
                    <td>{new Date(item.createdAt).toLocaleDateString("en-GB")}</td>
                    <td>{item.groupName ?? "Full account"}</td>
                    <td>{item.status}</td>
                    <td>
                      {(item.amountPaid || item.amountDue) / 100} {item.currency.toUpperCase()}
                    </td>
                    <td>
                      {item.invoicePdf ? (
                        <a href={item.invoicePdf} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      ) : item.hostedInvoiceUrl ? (
                        <a href={item.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </>
  );
}

function GroupBilling({ group, billing, busy, checkout, portal }: { group: BillingSummary["billableGroups"][number]; billing: BillingSummary; busy: string; checkout: (selection: CheckoutSelection, key: string) => Promise<void>; portal: () => Promise<void> }) {
  const subscription = billing.subscriptions.find((item) => item.groupId === group.id && current(item.status));
  const progress = Math.min(group.qualifyingMemberCount, 5);
  return (
    <section className="team-panel mb-3">
      <div className="admin-section-heading">
        <div>
          <h2 className="h5">{group.name}</h2>
          <p className="text-secondary small mb-0">{group.scheme === "community" ? "Private community group · free forever · two sponsored memberships included" : "Managed network · introductory £40/month for 6 months, then £60; 25 seats included"}</p>
        </div>
        {group.scheme === "community" ? <span className="status-chip status-active">Free forever</span> : subscription && <Status item={subscription} />}
      </div>
      {group.scheme === "community" ? (
        <p className="text-secondary small mt-3 mb-0">No group subscription is required.</p>
      ) : subscription ? (
        <button className="btn btn-outline-light mt-3" onClick={() => void portal()}>
          Manage subscription
        </button>
      ) : (
        <div className="d-flex gap-2 mt-3">
          <button
            className="btn btn-primary"
            disabled={!!busy}
            onClick={() =>
              void checkout(
                {
                  kind: "managed_network",
                  groupId: group.id,
                  interval: "month",
                },
                `${group.id}-month`,
              )
            }
          >
            £40 monthly · first 6 months
          </button>
          <button
            className="btn btn-outline-light"
            disabled={!!busy}
            onClick={() =>
              void checkout(
                {
                  kind: "managed_network",
                  groupId: group.id,
                  interval: "year",
                },
                `${group.id}-year`,
              )
            }
          >
            £480 · first year
          </button>
        </div>
      )}
    </section>
  );
}

function Status({ item }: { item: BillingSummary["subscriptions"][number] }) {
  return <span className={`status-chip status-${item.status === "active" || item.status === "trialing" ? "active" : "grace"}`}>{item.cancelAtPeriodEnd ? "Cancels at period end" : item.status.replaceAll("_", " ")}</span>;
}
function message(error: unknown) {
  return error instanceof ApiError ? error.message : "Unable to reach billing. Please try again.";
}
